import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type {
  EventKategori,
  EventKonsekvens,
  EventValg,
  SpillEvent,
} from '@/data/types';
import { BOKMESSER, type BokmesseId } from '@/systems/bokmesser';
import { FILM_TOP_BORDER_HEX, LYDBOK_TOP_BORDER_HEX } from '@/systems/lydbokOgFilm';
import { CALIDRIS_NAVN, erCalidrisEventId } from '@/systems/calidris';
import { autoSpeed } from '@/systems/autoSpeed';
import { animateModalGlide } from '@/ui/modalGlide';
import { tooltipManager } from '@/ui/TooltipManager';

const CALIDRIS_OKER_HEX = 0xb8860b;

const SHEET_WIDTH = 1000;
const SHEET_PADDING = 32;

const KATEGORI_NAVN: Record<EventKategori, string> = {
  'presse-og-kritikk': 'Presse og kritikk',
  'priser-og-utdelinger': 'Priser og utdelinger',
  'bokmesse-og-festival': 'Bokmesse og festival',
  'skandaler-og-kontrovers': 'Skandale',
  'kontrakts-forhandling': 'Kontrakt',
  'personlig-drama': 'Drama',
  'tidlig-spill': 'Forlagshverdagen',
};

const KATEGORI_BILDE: Partial<Record<EventKategori, string>> = {
  'priser-og-utdelinger': 'event_brage-nominasjon',
  'skandaler-og-kontrovers': 'event_plagiat-skandale',
  'kontrakts-forhandling': 'event_telefonen-ringer',
  'personlig-drama': 'event_telefonen-ringer',
};

export class EventCard {
  private layer?: Phaser.GameObjects.Container;
  private currentEventId?: string;

  constructor(private scene: Phaser.Scene) {}

  open(event: SpillEvent): void {
    if (this.layer && this.currentEventId === event.id) return;
    this.close();
    this.currentEventId = event.id;
    this.layer = this.scene.add.container(0, 0).setDepth(180);
    animateModalGlide(this.scene, this.layer);

    const erStjele = event.id.startsWith('synth_stjeleforsoek_');
    const erCalidris = erCalidrisEventId(event.id);
    const messeFarge =
      bokmesseAksent(event.id) ??
      lydbokFilmAksent(event.id) ??
      (erCalidris ? CALIDRIS_OKER_HEX : null);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 140;
    const sheetH = GAME_HEIGHT - 280;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.white, 1)
      .setOrigin(0, 0);
    if (erStjele) {
      sheet.setStrokeStyle(3, COLORS.wine, 1);
    }
    this.layer.add(sheet);

    if (messeFarge !== null) {
      const topBorder = this.scene.add
        .rectangle(sheetX, sheetY, SHEET_WIDTH, 12, messeFarge, 1)
        .setOrigin(0, 0);
      this.layer.add(topBorder);
    }

    const venstreBorder = this.scene.add
      .rectangle(sheetX, sheetY, 8, sheetH, messeFarge ?? COLORS.ochre, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    let y = sheetY + SHEET_PADDING;

    const badge = this.byggKategoriBadge(sheetX + SHEET_PADDING, y, event.kategori);
    this.layer.add(badge.container);
    y += badge.height + 24;

    const tittel = this.scene.add
      .text(sheetX + SHEET_PADDING, y, event.tittel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(44),
        color: '#862736',
        fontStyle: erCalidris ? 'italic' : undefined,
        wordWrap: { width: SHEET_WIDTH - SHEET_PADDING * 2 - 90 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);
    y += tittel.height + 24;

    if (erCalidris) {
      const merkelapp = this.scene.add
        .text(sheetX + SHEET_PADDING, y, CALIDRIS_NAVN, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          fontStyle: 'italic',
          color: '#8b5e3c',
        })
        .setOrigin(0, 0);
      this.layer.add(merkelapp);
      y += merkelapp.height + 12;
    }

    const bilde = this.byggBilde(sheetX + SHEET_PADDING, y, event.kategori, event.id);
    if (bilde) {
      this.layer.add(bilde);
      y += 200;
    }

    const beskrivelse = this.scene.add
      .text(sheetX + SHEET_PADDING, y, event.beskrivelse.trim(), {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#1a1a1a',
        wordWrap: { width: SHEET_WIDTH - SHEET_PADDING * 2 },
        lineSpacing: 8,
      })
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);
    y += beskrivelse.height + 28;

    for (let i = 0; i < event.valg.length; i += 1) {
      const valg = event.valg[i];
      const knapp = this.byggValgKnapp(
        sheetX + SHEET_PADDING,
        y,
        SHEET_WIDTH - SHEET_PADDING * 2,
        valg,
        i,
        event.id,
      );
      this.layer.add(knapp.container);
      y += knapp.height + 14;
    }

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - SHEET_PADDING, sheetY + 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => {
      gameState.lukkAktivtEvent();
      this.close();
    });
    this.layer.add(closeBtn);
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    this.currentEventId = undefined;
    autoSpeed.fortsett();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private byggKategoriBadge(
    x: number,
    y: number,
    kategori: EventKategori,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padX = 16;
    const padY = 8;
    const txt = this.scene.add
      .text(padX, padY, KATEGORI_NAVN[kategori] ?? kategori, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const w = txt.width + padX * 2;
    const h = txt.height + padY * 2;
    const bg = this.scene.add.rectangle(0, 0, w, h, COLORS.ochre, 1).setOrigin(0, 0);
    c.add([bg, txt]);
    return { container: c, height: h };
  }

  private byggBilde(
    x: number,
    y: number,
    kategori: EventKategori,
    _eventId: string,
  ): Phaser.GameObjects.Image | null {
    const key = KATEGORI_BILDE[kategori];
    if (!key) return null;
    if (!this.scene.textures.exists(key)) return null;
    const bilde = this.scene.add.image(x, y, key).setOrigin(0, 0);
    const src = this.scene.textures.get(key).getSourceImage() as HTMLImageElement;
    const maksH = 180;
    const skala = maksH / (src.height || maksH);
    bilde.setScale(skala);
    return bilde;
  }

  private byggValgKnapp(
    x: number,
    y: number,
    w: number,
    valg: EventValg,
    index: number,
    eventId: string,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padY = 16;
    const padX = 20;

    const tekst = this.scene.add
      .text(padX, padY, valg.tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#862736',
        fontStyle: '600',
        wordWrap: { width: w - padX * 2 },
      })
      .setOrigin(0, 0);
    const previewDeler = formaterKonsekvensPreview(valg.konsekvenser);
    const preview = previewDeler.lengde > 0
      ? this.scene.add
          .text(padX, padY + tekst.height + 6, previewDeler.tekst, {
            fontFamily: FONTS.hud,
            fontSize: fontStr(16),
            color: previewDeler.farge,
            wordWrap: { width: w - padX * 2 },
          })
          .setOrigin(0, 0)
      : null;

    const innerH = tekst.height + (preview ? preview.height + 6 : 0);
    const h = innerH + padY * 2;

    const bg = this.scene.add.rectangle(0, 0, w, h, 0xffffff, 1).setOrigin(0, 0);
    bg.setStrokeStyle(2, COLORS.wine, 1);

    c.add(bg);
    c.add(tekst);
    if (preview) c.add(preview);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => {
      bg.setFillStyle(COLORS.wine, 1);
      tekst.setColor('#ffffff');
      if (preview) preview.setColor('#f4ead5');
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(0xffffff, 1);
      tekst.setColor('#862736');
      if (preview) preview.setColor(previewDeler.farge);
    });
    bg.on('pointerup', () => this.velg(index));

    const tooltipTekst = byggKonsekvensTooltip(eventId, valg);
    if (tooltipTekst) {
      tooltipManager.attach(
        bg,
        () => tooltipTekst,
        () => ({ x: x + w / 2, y: y + 4 }),
      );
    }

    return { container: c, height: h };
  }

  private velg(index: number): void {
    gameState.velgEventValg(index);
    this.close();
  }
}

type PreviewResultat = { tekst: string; farge: string; lengde: number };

function formaterKonsekvensPreview(k: EventKonsekvens): PreviewResultat {
  const deler: string[] = [];
  let harNegativ = false;
  let harPositiv = false;
  if (typeof k.penger === 'number' && k.penger !== 0) {
    const tegn = k.penger > 0 ? '+' : '−';
    deler.push(`${tegn}${Math.abs(k.penger).toLocaleString('nb-NO')} kr`);
    if (k.penger > 0) harPositiv = true;
    else harNegativ = true;
  }
  if (typeof k.omdomme === 'number' && k.omdomme !== 0) {
    const tegn = k.omdomme > 0 ? '+' : '';
    deler.push(`${tegn}${k.omdomme} omdømme`);
    if (k.omdomme > 0) harPositiv = true;
    else harNegativ = true;
  }
  if (typeof k.attention === 'number' && k.attention !== 0) {
    const tegn = k.attention > 0 ? '+' : '';
    deler.push(`${tegn}${k.attention} oppmerksomhet`);
    if (k.attention > 0) harPositiv = true;
    else harNegativ = true;
  }
  if (typeof k.flag_set === 'string' && k.flag_set.length > 0) {
    deler.push(`flagg: ${k.flag_set.replace(/_/g, ' ')}`);
  }
  if (typeof k.risiko === 'string' && k.risiko.length > 0) {
    deler.push(`risiko: ${k.risiko.replace(/_/g, ' ')}`);
    harNegativ = true;
  }
  // Syntetiske nøkler som starter med _ vises ikke i preview
  // (brukes til intern routing, f.eks. _stjele_valg)

  let farge = '#5a4a3a';
  if (harNegativ && !harPositiv) farge = '#862736';
  else if (harPositiv && !harNegativ) farge = '#3d5a4d';

  return { tekst: deler.join(' · '), farge, lengde: deler.length };
}

function bokmesseAksent(eventId: string): number | null {
  if (!eventId.startsWith('synth_bokmesse_')) return null;
  const rest = eventId.slice('synth_bokmesse_'.length);
  let messeId: BokmesseId | null = null;
  if (rest.startsWith('lillehammer')) messeId = 'lillehammer';
  else if (rest.startsWith('frankfurt') || rest.startsWith('oversettelse')) {
    messeId = 'frankfurt';
  } else if (rest.startsWith('lithus')) messeId = 'litteraturhuset';
  if (!messeId) return null;
  const messe = BOKMESSER.find((m) => m.id === messeId);
  return messe?.topBorderHex ?? null;
}

function lydbokFilmAksent(eventId: string): number | null {
  if (eventId.startsWith('synth_lydbok_tilbud_')) return LYDBOK_TOP_BORDER_HEX;
  if (eventId.startsWith('synth_film_tilbud_')) return FILM_TOP_BORDER_HEX;
  return null;
}

function byggKonsekvensTooltip(eventId: string, valg: EventValg): string {
  const k = valg.konsekvenser;
  const linjer: string[] = [];

  if (typeof k.penger === 'number' && k.penger !== 0) {
    const tegn = k.penger > 0 ? '+' : '−';
    linjer.push(`${tegn}${Math.abs(k.penger).toLocaleString('nb-NO')} kr endring i kassen`);
  }
  if (typeof k.omdomme === 'number' && k.omdomme !== 0) {
    const retning = k.omdomme > 0 ? 'styrket' : 'svekket';
    linjer.push(`${retning} omdømme (${k.omdomme > 0 ? '+' : ''}${k.omdomme})`);
  }
  if (typeof k.attention === 'number' && k.attention !== 0) {
    linjer.push(`oppmerksomhet ${k.attention > 0 ? '+' : ''}${k.attention}`);
  }
  if (typeof k.forfatter_lojalitet === 'number' && k.forfatter_lojalitet !== 0) {
    linjer.push(`forfatter-lojalitet ${k.forfatter_lojalitet > 0 ? '+' : ''}${k.forfatter_lojalitet}`);
  }
  if (typeof k.forfatter_stress === 'number' && k.forfatter_stress !== 0) {
    linjer.push(`forfatter-stress ${k.forfatter_stress > 0 ? '+' : ''}${k.forfatter_stress}`);
  }
  if (typeof k.salg_boost === 'number' && k.salg_boost !== 0) {
    linjer.push(`salgsboost ${(k.salg_boost * 100).toFixed(0)}%`);
  }
  if (typeof k.flag_set === 'string' && k.flag_set.length > 0) {
    if (erCalidrisEventId(eventId)) {
      linjer.push(`${CALIDRIS_NAVN}-flag aktivt i 30 dager — flere henvendelser kommer.`);
    } else {
      linjer.push(`flagg «${k.flag_set.replace(/_/g, ' ')}» settes på din profil`);
    }
  }
  if (typeof k.risiko === 'string' && k.risiko.length > 0) {
    linjer.push(`risiko: «${k.risiko.replace(/_/g, ' ')}» kan utløse oppfølgings-event`);
  }
  if (typeof k.tid_brukt === 'string' || typeof k.tid_brukt === 'number') {
    const t = String(k.tid_brukt).replace(/_/g, ' ');
    linjer.push(`tidsbruk: ${t}`);
  }

  if (linjer.length === 0) return '';
  return `Hvis du velger dette:\n• ${linjer.join('\n• ')}`;
}
